import { Link, useLocation } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/auth-context";
import {
  canAccessReports,
  canManageBackOffice,
  canManageExperts,
  canReviewCommunications,
  isExpert,
  isPartnerWithoutLicense,
  showPartnerTab,
} from "@/lib/auth/roles";
import { cn } from "@/lib/utils/cn";

/** Back-office destinations, in header order. Gated per role below. */
type ManageTo =
  | "/experts/admin"
  | "/tools/admin"
  | "/financial-aids/admin"
  | "/formation-groups"
  | "/catalogue"
  | "/admin"
  | "/partner-group"
  | "/communication-review";

/**
 * Primary navigation. Three role-shaped variants (docs/roles-and-permissions.md):
 *
 * - **Expert** — no links at all; experts have their own restricted app.
 * - **Partner without a personal licence** — no member-journey tabs (they don't
 *   run the questionnaire); only their back-office destinations: Partner group,
 *   plus Administration when they can access reports.
 * - **Everyone else** (members, licensed partners, admins) — the four
 *   member-journey tabs (dashboard → self-assessment → action plans →
 *   achievement sharing) as first-class links, with the role-gated back-office
 *   destinations (Expert management, Administration, Partner group, Communication
 *   review) collapsed into a single "Management" dropdown on the desktop header so
 *   they don't crowd
 *   the journey tabs onto two lines. In the mobile drawer
 *   (`orientation="vertical"`) that group renders flat and labelled instead.
 *
 * `onNavigate` lets the drawer close itself when a link is tapped.
 */
export function Nav({
  orientation = "horizontal",
  onNavigate,
}: {
  orientation?: "horizontal" | "vertical";
  onNavigate?: () => void;
} = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();

  const vertical = orientation === "vertical";
  const linkClass = cn(
    "whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground",
    vertical && "min-h-touch flex items-center",
  );
  const activeProps = { className: cn(linkClass, "bg-secondary text-foreground") };
  const inactiveProps = { className: linkClass };

  const navClass = cn(vertical ? "flex flex-col gap-1" : "flex items-center gap-1");

  // Experts have their own restricted app — no header links at all.
  if (isExpert(user)) return null;

  // A partner without a personal licence doesn't run the questionnaire, so the
  // member-journey tabs (and dashboard) don't apply — show only their back-office
  // destinations: the partner group, and Administration if they can reach it.
  if (isPartnerWithoutLicense(user)) {
    return (
      <nav aria-label={t("shell.appName")} className={navClass} onClick={onNavigate}>
        <Link
          to="/partner-group"
          activeProps={activeProps}
          inactiveProps={inactiveProps}
        >
          {t("nav.partnerGroups")}
        </Link>
        {canAccessReports(user) && (
          <Link to="/admin" activeProps={activeProps} inactiveProps={inactiveProps}>
            {t("nav.reports")}
          </Link>
        )}
        {canReviewCommunications(user) && (
          <Link
            to="/communication-review"
            activeProps={activeProps}
            inactiveProps={inactiveProps}
          >
            {t("nav.communicationReview")}
          </Link>
        )}
      </nav>
    );
  }

  const manageItems = [
    canManageExperts(user) && { to: "/experts/admin", label: t("nav.expertAdmin") },
    canManageExperts(user) && { to: "/tools/admin", label: t("nav.toolAdmin") },
    canManageExperts(user) && {
      to: "/financial-aids/admin",
      label: t("nav.financialAidAdmin"),
    },
    // Training groups: the same gate the API applies (`is_admin`), so a
    // regional partner sees no entry and would be refused behind it anyway.
    canManageBackOffice(user) && {
      to: "/formation-groups",
      label: t("nav.formationGroups"),
    },
    // Questionnaire content: the same gate as its API (`is_admin`).
    canManageBackOffice(user) && { to: "/catalogue", label: t("nav.catalogue") },
    canAccessReports(user) && { to: "/admin", label: t("nav.reports") },
    showPartnerTab(user) && { to: "/partner-group", label: t("nav.partnerGroups") },
    canReviewCommunications(user) && {
      to: "/communication-review",
      label: t("nav.communicationReview"),
    },
  ].filter(Boolean) as { to: ManageTo; label: string }[];
  const manageActive = manageItems.some((it) => pathname.startsWith(it.to));

  return (
    <nav aria-label={t("shell.appName")} className={navClass} onClick={onNavigate}>
      <Link to="/dashboard" activeProps={activeProps} inactiveProps={inactiveProps}>
        {t("nav.dashboard")}
      </Link>
      <Link
        to="/questionnaire"
        activeProps={activeProps}
        inactiveProps={inactiveProps}
      >
        {t("nav.questionnaire")}
      </Link>
      <Link
        to="/action-plans"
        activeProps={activeProps}
        inactiveProps={inactiveProps}
      >
        {t("nav.actionPlans")}
      </Link>
      <Link
        to="/communications"
        activeProps={activeProps}
        inactiveProps={inactiveProps}
      >
        {t("nav.communications")}
      </Link>

      {manageItems.length > 0 &&
        (vertical ? (
          // Drawer: a flat, labelled group (a dropdown-in-a-drawer is awkward).
          <div className="mt-1 flex flex-col gap-1 border-t pt-2">
            <span className="px-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
              {t("nav.management")}
            </span>
            {manageItems.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                activeProps={activeProps}
                inactiveProps={inactiveProps}
              >
                {it.label}
              </Link>
            ))}
          </div>
        ) : (
          // Header: one dropdown so the back-office tools stay one click away
          // without competing for space with the journey tabs.
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  linkClass,
                  "inline-flex items-center gap-1",
                  manageActive && "bg-secondary text-foreground",
                )}
              >
                {t("nav.management")}
                <ChevronDown className="size-4" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {manageItems.map((it) => (
                <DropdownMenuItem key={it.to} asChild>
                  <Link to={it.to}>{it.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ))}
    </nav>
  );
}
